import { css } from "@emotion/react";
import { useMessage } from "@hermes/intl";
import { searchParamTypes, useSearchParams } from "@hermes/shared";
import {
  ButtonV2,
  type ComboboxImperativeRef,
  ComboBoxMultiSelect,
  icons,
  type ListBoxOptions,
  useComboboxMultiSelectState,
  useFormField,
  usePrincipalColors,
  useScreenDetector,
} from "@hermes/ui";
import { useRef } from "react";

import { useAppConfig } from "#internal/app/config/context";
import { LineCloseIcon } from "#internal/shared/ui/icons";

import type { DoctorsListParams } from "./types";

export const LanguageFilter = ({
  params,
  onParamsChange,
  variant,
}: {
  params: DoctorsListParams;
  onParamsChange: (params: DoctorsListParams) => void;
  variant?: "filter-search" | "filter-outlined";
}) => {
  const principalColors = usePrincipalColors();
  const { isMobile } = useScreenDetector();
  const message = useMessage();
  const { country } = useAppConfig();
  const lngComboRef = useRef<ComboboxImperativeRef>(null);

  const field = useFormField(
    useComboboxMultiSelectState({
      value: params.language
        ? country.doctorLocales
            .filter(
              ({ value }) =>
                !!params.language
                  ?.split(",")
                  .find((code: string) => code === value),
            )
            .map((lng) => ({
              key: lng,
              label: lng.title,
            }))
        : [],
    }),
  );

  return (
    <ComboBoxMultiSelect
      imperativeRef={lngComboRef}
      variant="column"
      stateless
      field={field}
      mobile={{
        subtitle: message({
          id: "c8176565-1299-4e34-b908-1eae7192580e",
          default: "Язык",
        }),
      }}
      desktop={{
        width: "200px",
      }}
      options={country.doctorLocales.map((lng) => ({
        key: lng,
        label: lng.title,
      }))}
      toKeyString={(lng) => lng.value}
      onSelectionChange={(option) => {
        const options = option?.map((opt) => opt.key.value);

        onParamsChange({
          language: options?.join(",") || undefined,
        });

        setTimeout(() => lngComboRef.current?.close(), 0);
      }}
      renderItem={LanguageOptionItem}
      renderSelectedItems={() => null}
      children={(inputRef, inputProps, triggerRef, triggerProps) => (
        <>
          <ButtonV2
            text={
              params.language
                ? `${message({ id: "c8176565-1299-4e34-b908-1eae7192580e" })}: ${params.language.split(",").length}`
                : message({
                    id: "c8176565-1299-4e34-b908-1eae7192580e",
                  })
            }
            variant={
              variant ? variant : isMobile ? "filter-search" : "filter-outlined"
            }
            active={!!params.language}
            triggerRef={triggerRef}
            triggerProps={triggerProps}
            icon={
              params.language ? (
                <LineCloseIcon
                  width={isMobile ? "20" : "24"}
                  height={isMobile ? "20" : "24"}
                  color={isMobile ? principalColors.white : principalColors.gs8}
                />
              ) : undefined
            }
            onIconPress={
              params.language
                ? () => {
                    onParamsChange({ language: undefined });
                    field.onChange({
                      ...field,
                      value: [],
                    });
                  }
                : undefined
            }
            styles={css({
              width: "auto",
              ...(!isMobile && {
                background:
                  triggerProps["aria-expanded"] ||
                  (field.value && field.value.length > 0)
                    ? principalColors.grayline
                    : principalColors.white,
              }),
            })}
          />
          <input ref={inputRef} {...inputProps} css={{ display: "none" }} />
        </>
      )}
    />
  );
};

const LanguageOptionItem: ListBoxOptions<any>["renderItem"] = ({
  option,
  rendered,
}) => {
  const [params] = useSearchParams(
    {
      language: searchParamTypes.string({}),
    },
    { allowUnknown: true },
  );

  const checked = params.language
    ? !!params.language
        ?.split(",")
        .find((code: string) => code === option.value)
    : false;

  return <Checkbox checked={checked} rendered={rendered} />;
};

const Checkbox = ({
  checked,
  rendered,
}: {
  checked: boolean;
  rendered: React.ReactNode;
}) => {
  const { isMobile } = useScreenDetector();
  const principalColors = usePrincipalColors();

  return (
    <div
      css={{
        padding: isMobile ? "0 10px" : "0 8px",
      }}
    >
      <div
        css={{
          display: "flex",
          alignItems: "flex-start",
          gap: 10,
          padding: isMobile ? "16px 0 15px" : "8px 0 8px",
          borderBottom: isMobile
            ? `1px solid ${principalColors.grayline}`
            : "none",
          svg: {
            minWidth: "24px",
            color: checked ? principalColors.ebblue : principalColors.grayline,
          },
          "&:hover": {
            cursor: "pointer",
          },
        }}
      >
        {checked ? <icons.CheckboxFilled /> : <icons.CheckboxNormal />}{" "}
        {rendered}
      </div>
    </div>
  );
};
